import Handlebars from 'handlebars';
/**
 * Regex for extracting variable names from templates. Matches:
 * - {{variable}}                             — plain
 * - {{variable | 'fallback'}}                — legacy pipe syntax
 * - {{default variable 'fallback'}}          — quoted literal fallback
 * - {{default variable otherVariable}}       — variable-to-variable fallback
 *
 * Two alternatives keep the "default var otherVar" form scoped to the
 * default-helper syntax so casual `{{two words}}` doesn't get treated as
 * two variables.
 */
const EXTRACT_REGEX = /\{\{default\s+(\w+)(?:\s*\|\s*'[^']*'|\s+'[^']*'|\s+(\w+))?\}\}|\{\{(\w+)(?:\s*\|\s*'[^']*')?\}\}/g;
/**
 * Extract variable names from text content using {{variable_name}} interpolation syntax.
 */
export function extractVariables(text) {
    if (!text?.trim())
        return [];
    const names = [];
    for (const m of text.matchAll(EXTRACT_REGEX)) {
        const primary = m[1] ?? m[3];
        if (primary)
            names.push(primary);
        if (m[2])
            names.push(m[2]);
    }
    return [...new Set(names)];
}
/**
 * Extract variables from multiple text fields and return unique variable names.
 */
export function extractVariablesFromTexts(...texts) {
    const allVariables = texts.flatMap(text => extractVariables(text ?? ''));
    return [...new Set(allVariables)];
}
/**
 * Test if a string contains any interpolation variables.
 */
export function containsVariables(text) {
    return extractVariables(text).length > 0;
}
/**
 * Migrate single-brace variables {var} to double-brace {{var}}.
 * Only converts {var} that are not already {{var}}.
 */
export function migrateSingleToDoubleBrace(text) {
    if (!text?.trim())
        return text;
    return text.replace(/(?<!\{)\{(?!\{)(\w+)\}(?!\})/g, '{{$1}}');
}
// Register Handlebars 'default' helper: {{default variable 'fallback'}}
Handlebars.registerHelper('default', (value, fallback) => value && typeof value === 'string'
    ? value
    : typeof fallback === 'string'
        ? fallback
        : '');
/**
 * Resolve {{variable}} placeholders in text using Handlebars.
 *
 * Supports:
 * - {{variable}} — replaced with value or empty string
 * - {{default variable 'fallback'}} — replaced with value or fallback
 * - {{variable | 'fallback'}} — legacy syntax, auto-migrated
 *
 * HTML escaping is disabled (noEscape) since these are used in
 * voice prompts, SMS, and email — not rendered as HTML.
 */
/**
 * Convert pipe fallback syntax to Handlebars default helper.
 * {{ var | 'fallback' }} → {{default var 'fallback'}}
 */
function convertPipeSyntax(text) {
    return text.replace(/\{\{\s*(\w+)\s*\|\s*'([^']*)'\s*\}\}/g, (_match, name, fallback) => `{{default ${name} '${fallback}'}}`);
}
export function resolveVariables(text, variableValues) {
    if (!text?.trim())
        return text;
    try {
        const migrated = convertPipeSyntax(text);
        const template = Handlebars.compile(migrated, { noEscape: true });
        return template(variableValues);
    }
    catch {
        return text.replace(/\{\{(\w+)\}\}/g, (_, key) => variableValues[key] ?? '');
    }
}
//# sourceMappingURL=variable-extraction.js.map